import { useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AskQuestionForm } from "@/components/AskQuestionForm";

const DEMO_VIDEO = "https://customer-assets-39nsmqrw.emergentagent.net/job_inventory-hub-3030/artifacts/3d4hx28r_Orbit%20%E2%80%94%20One%20Scan%20v1%20%281%29.mp4";

const BRANDED_DESIGNS = [
  { key: "vehicle", name: "Vehicle asset tag", size: '4" × 2"', alt: "Orbit vehicle label: Scan to view asset with QR code and Stock #, Year, Make, Model, VIN, Miles fields" },
  { key: "construction", name: "Construction equipment", size: '4" × 4" · 2" × 2"', alt: "Orbit construction equipment label: Scan to view asset with excavator icon — Location, Hours, Maintenance, Service History, Notes" },
  { key: "realestate", name: "Real estate listing", size: '4" × 4" · 2" × 2"', alt: "Orbit real estate label: Scan to view listing with house icon — Photos, Details, Map, Contact" },
];

const LABEL_GROUPS = [
  { key: "sheet", title: "Sheet labels", badge: "Office laser printer", items: [
    { key: "uline-2x2", size: '2" × 2"', name: "Uline S-20177 2×2 sheet", use: "Weather-resistant laser label — the standard square Orbit label for most assets." },
    { key: "sheet-3x1", size: '3" × 1"', name: "3×1 sheet (20-up)", use: "Compact laser label for tools, parts, cases and small equipment." },
    { key: "sheet-4x2", size: '4" × 2"', name: "Uline S-5962 / Avery 5163 4×2 sheet", use: "Logo and description on the left, large QR on the right — 10 per sheet." },
    { key: "print-sheet", size: "Grid", name: "Plain-paper QR sheet", use: "2–4 QR codes per row on plain paper for quick tags and temporary use." },
  ] },
  { key: "roll", title: "Roll labels & signs", badge: "Thermal printer", items: [
    { key: "thermal-4x4", size: '4" × 4"', name: "4×4 thermal transfer", use: "Big windshield and property label — multipurpose paper stock, wax or wax/resin ribbon." },
    { key: "roll-2x2", size: '2" × 2"', name: "Uline S-21870 2×2 2-up roll", use: "Industrial thermal transfer, two across — durable square label for equipment and vehicles." },
    { key: "roll-3x1", size: '3" × 1"', name: "Uline S-18447 3×1 weatherproof roll", use: "Polyester, resin ribbon — outdoor use on vehicles, trailers and lock boxes." },
    { key: "sign", size: "Letter", name: "Open House / Scan sign", use: "Full-page “Scan for details” sign with a large QR for yard signs, lots and lobbies." },
  ] },
];

const NAVY = "#102c42", BLUE = "#176fe4", MINT = "#c8f275", MUTED = "#607281", LINE = "#dce5e9", BG = "#f4f7f8";

const INDUSTRIES = [
  { tab: "Dealerships", img: "auto", eyebrow: "For automotive teams", h: "Spend less time walking the lot.", p: "Connect a vehicle label to stock information and movement history so your team knows where to start.", items: ["VIN, stock number, photos, and vehicle details", "Recorded location and accountable transfers", "Inventory imports and nightly website sync"] },
  { tab: "Construction", img: "construction", eyebrow: "For construction teams", h: "Keep the next job moving.", p: "Connect tools and heavy equipment to the records your crew needs across job sites.", items: ["Serial numbers and equipment details", "Movement history between recorded locations", "Photos and supporting documents"] },
  { tab: "Property", img: "realestate", eyebrow: "For property teams", h: "Put property details within reach.", p: "Use QR labels and sign-post labels to connect people to listing information and keep property records organized.", items: ["Property and listing details", "Photos and attached documents", "Records across multiple locations"] },
];

const STEPS = [
  { n: "01 / Label", h: ["Give every asset", "its own starting point."], p: "Apply a unique Orbit QR label. Print in-house, order printed labels, or arrange on-site installation." },
  { n: "02 / Scan", h: ["Open the details", "right where you work."], p: "Use a phone to access the asset record, including its photos, documents, status, and recorded location." },
  { n: "03 / Track", h: ["Keep the next person", "in the loop."], p: "Record movement and keep a history of who moved the asset, where it went, and when." },
];

const FEATURES = [
  { h: "Details in one place", p: "Industry-specific fields, photos, and documents help keep asset records complete." },
  { h: "A history you can follow", p: "A movement ledger records user, location, and time for asset transfers." },
  { h: "Reports you can use", p: "Review inventory and activity, then export reports to CSV." },
  { h: "Access for your team", p: "Company accounts and user roles help control who can view and update records." },
];

const PLANS = [
  { name: "Basic", price: "$49", p: "An entry point for your inventory workflow." },
  { name: "Essential", price: "$149", p: "Explore the next level of Orbit's plans." },
  { name: "Pro", price: "$299", p: "Review features for your team's needs.", featured: true },
  { name: "Enterprise", price: "$499+", p: "Talk through your operation with Orbit." },
];

const FAQS = [
  { q: "Does a QR label continuously track location?", a: "A QR label connects to an asset record. Orbit's movement workflow records location updates and who made them. A QR label by itself is not a continuous GPS tracker." },
  { q: "Can Orbit print and install our labels?", a: "Yes. Orbit offers Print & Ship and Print & Install services, plus large-volume installation pricing. Request a quote to confirm your location, label material, and quantities." },
  { q: "Can we use our existing inventory information?", a: "Orbit supports website-link and PDF imports, with nightly sync. Book a demo to confirm compatibility with your source and workflow." },
  { q: "What will we cover in a demo?", a: "Bring your asset types, approximate quantities, locations, and current process. We'll discuss the scanning workflow, user access, label options, and the plan that fits." },
];

const Eyebrow = ({ children, mint }) => <div className="text-[11px] uppercase tracking-[2px] font-bold" style={{ color: mint ? MINT : BLUE }}>{children}</div>;
const Btn = ({ to, href, light, dark, children, className = "", ...rest }) => {
  const cls = `inline-flex items-center justify-center gap-3 rounded-[7px] px-6 py-3.5 text-sm font-bold border transition-all hover:-translate-y-0.5 ${className}`;
  const style = light ? { background: "#fff", color: NAVY, borderColor: LINE } : dark ? { background: NAVY, color: "#fff", borderColor: NAVY } : { background: BLUE, color: "#fff", borderColor: BLUE };
  if (to) return <Link to={to} className={cls} style={style} {...rest}>{children}</Link>;
  return <a href={href} className={cls} style={style} {...rest}>{children}</a>;
};

function HeroPhoto({ onPlay }) {
  return (
    <button type="button" onClick={onPlay} className="group relative block w-full rounded-[18px] p-2.5 text-left transition-transform hover:-translate-y-0.5" style={{ background: "#e6edf1" }} data-testid="concept-hero-photo" aria-label="Watch the 20-second demo">
      <img src="/hero-demo.jpg" alt="A 4×4 QR label on a truck windshield next to a phone showing the vehicle's Orbit record" className="w-full rounded-[12px] block" style={{ boxShadow: "0 22px 50px #143c5014" }} />
      <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-16 w-16 rounded-full bg-white/95 shadow-lg flex items-center justify-center group-hover:scale-105 transition-transform">
        <svg width="26" height="26" viewBox="0 0 24 24" fill={BLUE} aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
      </span>
      <span className="absolute left-5 bottom-5 rounded-md bg-white/95 px-3 py-1.5 text-[11px] font-bold tracking-wide shadow" style={{ color: NAVY }}>▶ WATCH THE 20-SECOND DEMO</span>
    </button>
  );
}

function InteractiveDemo() {
  const [moved, setMoved] = useState(false);
  return (
    <div id="demo" className="relative rounded-[18px] p-5 lg:p-[22px] w-full max-w-[530px] lg:max-w-none mx-auto scroll-mt-24" style={{ background: "#e6edf1" }} data-testid="hero-demo">
      <div className="flex justify-between text-[10px] tracking-[1.4px] mb-4"><strong>ONE LABEL. THE WHOLE RECORD.</strong><span>INTERACTIVE EXAMPLE</span></div>
      <div className="bg-white rounded-[11px] overflow-hidden border" style={{ borderColor: "#d8e1e6", boxShadow: "0 22px 50px #143c5014" }}>
        <div className="text-white px-5 py-3.5 flex justify-between text-xs" style={{ background: NAVY }}><strong>ORBIT / Asset record</strong><span>● Connected</span></div>
        <div className="p-6">
          <div className="h-[140px] rounded-lg overflow-hidden mb-4" style={{ background: "#eff4f7" }}>
            <img src="/demo-ram.jpg" alt="2026 Ram 2500 on the lot with its Orbit QR windshield label" className="w-full h-full object-cover object-center" data-testid="demo-asset-photo" />
          </div>
          <span className="inline-block rounded-full text-[10px] font-bold px-2.5 py-1" style={{ background: "#eaf5da", color: "#43621f" }}>AVAILABLE</span>
          <h3 className="text-[23px] font-bold leading-tight my-1.5">2026 Ram 2500</h3>
          <div className="text-xs" style={{ color: MUTED }}>Stock #260142 · Illustrative inventory</div>
          <div className="grid grid-cols-2 gap-3 my-5">
            <div className="p-2.5 text-[13px]" style={{ background: "#f6f8fa" }}><span className="block text-[10px] uppercase tracking-wider" style={{ color: MUTED }}>Current location</span><strong data-testid="demo-location">{moved ? "Service department" : "Main lot · Row B"}</strong></div>
            <div className="p-2.5 text-[13px]" style={{ background: "#f6f8fa" }}><span className="block text-[10px] uppercase tracking-wider" style={{ color: MUTED }}>Recorded mileage</span>128 miles</div>
          </div>
          <div className="border-t pt-3.5 text-xs" style={{ borderColor: LINE }} data-testid="demo-event">
            {moved ? "Latest movement: Main lot → Service" : "Latest movement: Arrival → Main lot"}
            <small className="block" style={{ color: MUTED }}>{moved ? "Recorded by Example User · Just now" : "Recorded by Example User · Today, 9:10 AM"}</small>
          </div>
          <button type="button" onClick={() => setMoved((m) => !m)} className="w-full mt-5 rounded-[7px] px-6 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5" style={{ background: NAVY }} data-testid="demo-move">
            {moved ? "Try it: return vehicle to Main lot →" : "Try it: move vehicle to Service →"}
          </button>
          <p className="text-[10px] text-center mt-2 mb-0" style={{ color: MUTED }}>Sample interaction. No live inventory is changed.</p>
        </div>
      </div>
      <div className="hidden sm:block absolute bg-white rounded-[10px] p-3 text-[10px] text-center -rotate-[7deg] left-[-10px] lg:left-[-28px] bottom-20" style={{ boxShadow: "0 10px 30px #102c4220" }} data-testid="demo-sticker">
        <img src="/sticker-qr.png" alt="Scannable QR code linking to orbitcodes.com" className="w-[84px] h-[84px] block mx-auto mb-1.5" style={{ imageRendering: "pixelated" }} />
        STOCK #260142<br /><strong>SCAN. LEARN. TRACK.</strong>
      </div>
    </div>
  );
}

function Faq({ q, a, i }) {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-b py-5" style={{ borderColor: LINE }} data-testid={`faq-${i}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="w-full text-left font-bold text-base flex justify-between gap-4" data-testid={`faq-toggle-${i}`}>{q}<span style={{ color: BLUE }}>{open ? "−" : "+"}</span></button>
      {open && <p className="text-sm mt-3 mb-0" style={{ color: MUTED }}>{a}</p>}
    </div>
  );
}

export default function HomeConcept() {
  const [ind, setInd] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);
  const d = INDUSTRIES[ind];
  return (
    <div className="min-h-screen" style={{ color: NAVY, fontFamily: "Arial, Helvetica, sans-serif", background: "#fff" }} data-testid="home-concept">
      <nav className="sticky top-0 z-40 border-b" style={{ background: "#fffefcf5", borderColor: LINE }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-[75px] sm:h-[86px] flex items-center gap-3 sm:gap-7">
          <Link to="/" className="mr-auto flex items-center gap-2 font-extrabold text-[25px] sm:text-[30px] tracking-[-1.8px] leading-none" aria-label="Orbit Inventory Management home">
            <img src="/orbit-icon.jpg" alt="" className="w-10 h-10 object-contain" />
            <span>ORBIT<small className="block text-[9px] tracking-[1.7px] font-bold">INVENTORY MANAGEMENT</small></span>
          </Link>
          <a href="#how" className="hidden md:block text-[13px] font-semibold">How it works</a>
          <a href="#industries" className="hidden md:block text-[13px] font-semibold">Industries</a>
          <a href="#labels" className="hidden md:block text-[13px] font-semibold">Labels</a>
          <a href="#pricing" className="hidden md:block text-[13px] font-semibold">Pricing</a>
          <a href="#contact" className="hidden md:block text-[13px] font-semibold">Contact</a>
          <Link to="/videos" className="hidden md:block text-[13px] font-semibold">Videos</Link>
          <Link to="/login" className="hidden md:block text-[13px] font-semibold" data-testid="concept-login">Sign in ↗</Link>
          <Btn to="/get-demo" className="!px-3 !py-2.5 !text-[11px] sm:!px-6 sm:!py-3.5 sm:!text-sm" data-testid="concept-nav-demo">Book a demo ↗</Btn>
        </div>
      </nav>

      <header className="pt-11 pb-14 lg:pt-[74px]" style={{ background: "linear-gradient(110deg,#f6f9fb,#fff)" }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.04fr_1fr] items-center gap-9 lg:gap-[60px]">
          <div>
            <Eyebrow>QR labels. Connected inventory. Clear answers.</Eyebrow>
            <h1 className="font-extrabold leading-[1.06] tracking-[-2px] sm:tracking-[-3px] my-5" style={{ fontSize: "clamp(44px,5.2vw,70px)" }} data-testid="concept-hero-heading">Access More<br />with <span style={{ color: BLUE }}>One Scan.</span></h1>
            <p className="text-[17px] max-w-[475px]" style={{ color: MUTED }}>Give every vehicle, tool, or piece of equipment a label that connects your team to the details they need—right from their phone.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <button type="button" onClick={() => setDemoOpen(true)} className="inline-flex items-center justify-center gap-3 rounded-[7px] px-6 py-3.5 text-sm font-bold border text-white transition-all hover:-translate-y-0.5" style={{ background: BLUE, borderColor: BLUE }} data-testid="concept-hero-demo">▶ Watch the 20-second demo</button>
              <Btn to="/get-demo" light data-testid="concept-hero-book">Book a 15-minute demo</Btn>
            </div>
            <p className="text-xs mt-5" style={{ color: MUTED }}><strong style={{ color: NAVY }}>Software from $49/month.</strong> No per-asset software fees.<br />Print your own labels, or let Orbit print and install them.</p>
          </div>
          <HeroPhoto onPlay={() => setDemoOpen(true)} />
        </div>
      </header>

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-zinc-800" data-testid="concept-demo-dialog">
          <DialogHeader className="sr-only"><DialogTitle>Orbit 20-second demo</DialogTitle><DialogDescription>One scan on the lot — product demonstration video</DialogDescription></DialogHeader>
          {demoOpen && <video src={DEMO_VIDEO} poster="/demo-poster.jpg" controls autoPlay playsInline className="w-full aspect-video bg-black" data-testid="concept-demo-video" />}
          <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm" style={{ background: NAVY, color: "#b8cbd8" }}>
            <span>One Scan on the Lot · 20 seconds</span>
            <Link to="/videos" className="font-bold" style={{ color: MINT }}>More videos →</Link>
          </div>
        </DialogContent>
      </Dialog>

      <div className="py-6 border-b bg-white" style={{ borderColor: LINE }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 flex flex-wrap justify-between gap-x-5 gap-y-2 text-xs font-bold">
          {["Scan with your phone", "Track who moved it and when", "Keep photos and documents together", "Printing & installation available"].map((t) => <span key={t}><span className="mr-2.5" style={{ color: BLUE }}>✓</span>{t}</span>)}
        </div>
      </div>

      <section className="py-14 lg:py-[85px]" style={{ background: BG }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.04fr] items-center gap-9 lg:gap-[60px]">
          <div>
            <Eyebrow>Interactive example</Eyebrow>
            <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-6">One label.<br />The whole record.</h2>
            <p style={{ color: MUTED }}>This is what your team sees after a scan: status, location, mileage and the latest movement. Press the button to record a move and watch the ledger update — no live inventory is changed.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Btn to="/register" data-testid="concept-example-register">Start free</Btn>
              <Btn to="/get-demo" light data-testid="concept-example-book">Book a 15-minute demo</Btn>
            </div>
          </div>
          <InteractiveDemo />
        </div>
      </section>

      <section id="how" className="py-14 lg:py-[85px] scroll-mt-20">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="max-w-[630px]">
            <Eyebrow>From a label to a clear answer</Eyebrow>
            <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-6">Your inventory shouldn't<br />slow your team down.</h2>
            <p style={{ color: MUTED }}>Replace faded writing, scattered records, and "who moved it?" conversations with a consistent way to find and update asset information.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 lg:gap-9 mt-10">
            {STEPS.map((s) => (
              <div key={s.n} className="border-t pt-6" style={{ borderColor: LINE }}>
                <span className="text-xs uppercase" style={{ color: BLUE }}>{s.n}</span>
                <h3 className="text-[22px] font-bold leading-[1.3] mt-2">{s.h[0]}<br />{s.h[1]}</h3>
                <p className="text-sm mt-2" style={{ color: MUTED }}>{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="industries" className="py-14 lg:py-[85px] text-white scroll-mt-20" style={{ background: NAVY }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-9 lg:gap-20 items-start">
          <div>
            <Eyebrow mint>One platform. Your workflow.</Eyebrow>
            <h2 className="text-[33px] lg:text-[44px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-6">Built around the things<br />your business moves.</h2>
            <p style={{ color: "#b8cbd8" }}>From a dealership lot to a construction site, Orbit puts the right asset information within reach.</p>
            <div className="flex flex-wrap gap-2 my-7" role="tablist" aria-label="Industry examples">
              {INDUSTRIES.map((x, i) => (
                <button key={x.tab} type="button" role="tab" aria-selected={ind === i} onClick={() => setInd(i)} className="rounded-[5px] px-3.5 py-2 text-xs border transition-colors" style={ind === i ? { background: MINT, color: NAVY, borderColor: MINT } : { background: "transparent", color: "#c8d5df", borderColor: "#486273" }} data-testid={`industry-tab-${x.tab.toLowerCase()}`}>{x.tab}</button>
              ))}
            </div>
            <Link to="/register" className="text-sm font-bold" style={{ color: MINT }} data-testid="concept-industries-register">Start free with your industry preset ↗</Link>
          </div>
          <div className="rounded-xl p-6 lg:p-8 border min-h-[280px]" style={{ background: "#1c3b51", borderColor: "#345166" }} role="tabpanel" data-testid="industry-panel">
            <div className="flex items-start gap-4">
              <img key={d.img} src={`/industry/${d.img}.jpg`} alt={`${d.tab} example with Orbit QR label`} className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border shrink-0" style={{ borderColor: "#345166" }} data-testid="industry-photo" />
              <div className="min-w-0">
                <Eyebrow mint>{d.eyebrow}</Eyebrow>
                <h3 className="text-[22px] sm:text-[25px] font-bold leading-[1.3] mt-2 mb-3">{d.h}</h3>
              </div>
            </div>
            <p className="mt-3" style={{ color: "#b8cbd8" }}>{d.p}</p>
            <ul className="mt-4">{d.items.map((it) => <li key={it} className="my-3.5 text-sm"><span className="mr-3" style={{ color: MINT }}>↗</span>{it}</li>)}</ul>
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-[85px]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="max-w-[630px]">
            <Eyebrow>More than a QR code</Eyebrow>
            <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-6">The record behind the label<br />does the real work.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7 mt-10">
            {FEATURES.map((f) => (
              <div key={f.h} className="border-t pt-5" style={{ borderColor: LINE }}>
                <span style={{ color: BLUE }}>↗</span>
                <h3 className="text-[17px] font-bold mt-2">{f.h}</h3>
                <p className="text-[13px] mt-1" style={{ color: MUTED }}>{f.p}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Btn to="/videos" light data-testid="concept-videos">Watch the videos</Btn>
          </div>
        </div>
      </section>

      <section id="labels" className="py-14 lg:py-[85px] scroll-mt-20 bg-white border-y" style={{ borderColor: LINE }} data-testid="concept-labels">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="max-w-[640px]">
            <Eyebrow>Label formats</Eyebrow>
            <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-4">Every label we print, in one place.</h2>
            <p style={{ color: MUTED }}>Print any of these yourself from the QR Codes page on your own laser or thermal printer, or have us print, ship and install them. Pick sheet labels for the office printer, roll labels for a thermal printer.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {LABEL_GROUPS.map((g) => (
              <div key={g.title} className="rounded-xl border p-6" style={{ borderColor: LINE, background: BG }} data-testid={`label-group-${g.key}`}>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-bold">{g.title}</h3>
                  <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: "#e3eefb", color: BLUE }}>{g.badge}</span>
                </div>
                <ul className="divide-y" style={{ borderColor: LINE }}>
                  {g.items.map((l) => (
                    <li key={l.name} className="py-3 flex items-start gap-3" data-testid={`label-item-${l.key}`}>
                      <span className="shrink-0 mt-0.5 inline-flex items-center justify-center rounded-md text-[11px] font-bold px-2 py-1 border bg-white" style={{ borderColor: LINE, color: NAVY, minWidth: 64 }}>{l.size}</span>
                      <div className="min-w-0">
                        <p className="font-bold text-sm leading-tight">{l.name}</p>
                        <p className="text-xs mt-0.5" style={{ color: MUTED }}>{l.use}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
              <div>
                <h3 className="text-xl font-bold">Orbit branded label designs</h3>
                <p className="text-sm mt-1" style={{ color: MUTED }}>Ready-made "Scan to view asset" designs with your QR code, industry icon and field list — print them from the QR Codes page in 4×4 or the 4×2 vehicle card.</p>
              </div>
              <Btn to="/login" light data-testid="labels-print-branded">Print branded labels</Btn>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="branded-gallery">
              {BRANDED_DESIGNS.map((d) => (
                <figure key={d.key} className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: LINE }} data-testid={`branded-design-${d.key}`}>
                  <img src={`/labels/${d.key}.jpg`} alt={d.alt} className="w-full block" loading="lazy" />
                  <figcaption className="px-4 py-3 text-sm border-t flex items-center justify-between gap-3" style={{ borderColor: LINE }}>
                    <span className="font-bold">{d.name}</span><span className="text-xs" style={{ color: MUTED }}>{d.size}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Btn to="/request-quote" data-testid="labels-quote">Get labels printed & shipped</Btn>
            <Btn to="/pricing" light data-testid="labels-pricing">See label service pricing</Btn>
            <p className="text-xs" style={{ color: MUTED }}>Print & Ship $1.00/label · Print & Install $3.99/asset · Volume on-site $2.99–$3.49/asset</p>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-14 lg:py-[85px] scroll-mt-20" style={{ background: BG }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-20 items-start">
            <div>
              <Eyebrow>Clear starting points</Eyebrow>
              <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-2">Simple monthly software.<br />Flexible label services.</h2>
            </div>
            <div>
              <p style={{ color: MUTED }}>Choose software for your operation and the label service that fits your team. No per-asset software fees.</p>
              <Link to="/pricing" className="font-bold" style={{ color: BLUE }} data-testid="concept-pricing-link">Compare plan features and terms ↗</Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-9">
            {PLANS.map((p) => (
              <Link to="/pricing" key={p.name} className="bg-white rounded-[10px] p-6 border block hover:-translate-y-0.5 transition-transform" style={{ borderColor: p.featured ? BLUE : LINE, borderWidth: p.featured ? 2 : 1 }} data-testid={`concept-plan-${p.name.toLowerCase()}`}>
                <h3 className="text-[15px] font-bold">{p.name}</h3>
                <strong className="text-[35px] tracking-[-1px] font-extrabold">{p.price}</strong><small style={{ color: MUTED }}> / month</small>
                <p className="text-xs mt-2" style={{ color: MUTED }}>{p.p}</p>
              </Link>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-x-9 gap-y-2 text-[13px]">
            <span><strong>Print &amp; Ship:</strong> $1.00/label + shipping</span>
            <span><strong>Print &amp; Install:</strong> $3.99/asset</span>
            <span><strong>Large-volume installation:</strong> <Link to="/request-quote" className="font-bold" style={{ color: BLUE }} data-testid="concept-quote-link">ask for a quote ↗</Link></span>
          </div>
          <p className="text-[11px] mt-3" style={{ color: MUTED }}>Plan features, shipping, service area, and installation details are available on the full pricing page.</p>
        </div>
      </section>

      <section id="contact" className="py-14 lg:py-[85px] scroll-mt-20" style={{ background: BG }} data-testid="concept-contact">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.4fr] gap-9 lg:gap-20 items-start">
          <div>
            <Eyebrow>Ask a question</Eyebrow>
            <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-6">Not sure yet?<br />Just ask.</h2>
            <p style={{ color: MUTED }}>Send a quick note and we'll reply by email within one business day — no sales pressure. Prefer to talk? Call <a href="tel:+13373595408" className="font-bold" style={{ color: NAVY }}>337-359-5408</a>.</p>
          </div>
          <AskQuestionForm />
        </div>
      </section>

      <section className="py-14 lg:py-[85px]">
        <div className="max-w-[800px] mx-auto px-5 sm:px-8">
          <Eyebrow>Before you get started</Eyebrow>
          <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-4 mb-6">A few useful answers.</h2>
          {FAQS.map((f, i) => <Faq key={f.q} {...f} i={i} />)}
        </div>
      </section>

      <section className="pb-14 lg:pb-[85px]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="rounded-[18px] p-8 lg:p-[50px] flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-9" style={{ background: "#e7f0fb" }}>
            <div>
              <Eyebrow>Scan. Learn. Track. Move Forward.</Eyebrow>
              <h2 className="text-[33px] lg:text-[40px] leading-[1.15] tracking-[-1.5px] font-bold mt-3 mb-3 max-w-[600px]">See what Orbit could<br />simplify for your team.</h2>
              <p className="mb-0" style={{ color: MUTED }}>Start with a 15-minute conversation about your inventory.</p>
            </div>
            <Btn to="/get-demo" data-testid="concept-cta-demo">Book your demo ↗</Btn>
          </div>
        </div>
      </section>

      <footer className="border-t py-9 text-xs" style={{ borderColor: LINE }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 flex flex-wrap justify-between gap-6">
          <div><strong>ORBIT INVENTORY MANAGEMENT</strong><br />© {new Date().getFullYear()} · orbitcodes.com</div>
          <div className="space-y-1">
            <div className="flex flex-wrap gap-x-4"><a href="tel:+13373595408">337-359-5408</a><a href="mailto:Robert@orbitcodes.com">Robert@orbitcodes.com</a></div>
            <div className="flex flex-wrap gap-x-4"><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/request-quote">Request a quote ↗</Link><Link to="/prospect-score">Prospect scoring</Link><Link to="/login">Sign in</Link></div>
          </div>
        </div>
      </footer>
    </div>
  );
}
